import { Fragment } from "react";
import CButton from "components/CButton";
import color from "context/MaterialUITheme/color";
import CTermsDialog from "components/CTermsDialog";
import CPrivacyDialog from "components/CPrivacyDialog";
import CAddressAutocomplete from "components/CAddressAutocomplete";
import useRegisterFormCoborrower from "./useRegisterFormCoborrower";
import {
  Box,
  Link,
  List,
  Checkbox,
  MenuItem,
  useTheme,
  ListItem,
  Container,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CDialog from "components/CDialog";
import CTextField from "components/CTextField";
import CNumberField from "components/CNumberField";
import CPasswordField from "components/CPasswordField";
import { passwordMsgHelper, regexPassword } from "CONST";
import { useInvite } from "context/Invite/useInviteContext";
import CVerificationDialog from "components/CVerificationDialog";
import useUser from "context/UserCustomer/useUserCustomerContext";
import CCommunicationsDialog from "components/CCommunicationsDialog";
import LoanOfficerInformation from "components/LoanOfficerInformation";

const CoborrowerRegisterForm = () => {
  const user = useUser();
  const theme = useTheme();
  const invite = useInvite();
  const form = useRegisterFormCoborrower(
    invite?.email?.trim(),
    invite?.firstName,
    invite?.borrowerFullName
  );

  return (
    <Fragment>
      <Container
        maxWidth="md"
        disableGutters={useMediaQuery(theme.breakpoints.only("xs"))}
      >
        <Box>
          <Typography
            mt={4}
            mb={5}
            variant="h1"
            lineHeight={1}
            color={color.black}
            letterSpacing={-0.8}
            px={{ xs: 2, md: 0 }}
            fontSize={{ xs: "3.2rem", md: "5.2rem" }}
          >
            <span style={{ color: color.purple }}>
              Hello {form.watch("firstName")}.
            </span>
            <br /> Register here.
          </Typography>
          <Typography
            mb={2}
            display={"flex"}
            variant="subtitle1"
            px={{ xs: 2, md: 0 }}
            justifyContent={"center"}
          >
            Please complete the steps below to begin the co-borrower process for
            the loan requested on {invite?.addressLine}
          </Typography>
        </Box>
        {invite?.loanOfficer?.firstName && (
          <LoanOfficerInformation
            nmls={invite.loanOfficer.NMLSNumber}
            company={invite.broker?.lenderName}
            lastName={invite.loanOfficer.lastName}
            borrowerName={invite.firstName}
            avatar={invite.loanOfficer.avatar}
            companyNMLS={invite.broker?.NMLScompany}
            firstName={invite.loanOfficer?.firstName}
          />
        )}
        <Box
          borderRadius={10}
          bgcolor={color.white}
          sx={{ scrollbarWidth: "none" }}
        >
          <Box px={{ xs: 2, md: 6 }} py={{ xs: 4, md: 5 }}>
            <form onSubmit={form.onSubmit}>
              <CTextField
                error={
                  !invite?.borrowerFullName
                    ? "Primary Borrower is required."
                    : undefined
                }
                fullWidth
                type="text"
                value={invite?.borrowerFullName}
                label="Primary Borrower"
                placeholder="John Doe"
                disabled
                sx={{ marginBottom: 3 }}
              />

              <CTextField
                error={form.formState.errors.firstName?.message}
                fullWidth
                type="text"
                label="Legal First Name"
                placeholder="John"
                sx={{ marginBottom: 3 }}
                {...form.register("firstName", {
                  required: {
                    value: true,
                    message: "First Name is required.",
                  },
                  minLength: {
                    value: 2,
                    message:
                      "The First Name must be at least 2 characters long.",
                  },
                  maxLength: {
                    value: 120,
                    message:
                      "The First Name cannot be longer than 120 characters.",
                  },
                  pattern: {
                    value:
                      /^[\w'\-,.][^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<,>;:[\]]{2,}$/,
                    message: "The First Name format is incorrect.",
                  },
                })}
              />

              <CTextField
                fullWidth
                type="text"
                label="Legal Last Name"
                placeholder="Doe"
                sx={{ marginBottom: 3 }}
                error={form.formState.errors.lastName?.message}
                {...form.register("lastName", {
                  required: {
                    value: true,
                    message: "Last Name is required.",
                  },
                  minLength: {
                    value: 2,
                    message:
                      "The Last Name must be at least 2 characters long.",
                  },
                  maxLength: {
                    value: 120,
                    message:
                      "The Last Name cannot be longer than 120 characters.",
                  },
                  pattern: {
                    value: /^[a-zA-Z]{2,254}[ a-zA-Z ]/i,
                    message: "The Last Name format is incorrect.",
                  },
                })}
              />

              <CNumberField
                fullWidth
                label="Phone Number"
                placeholder="+1(123)-123-1234"
                format={
                  process.env.REACT_APP_ENV === "prod"
                    ? "+1###############"
                    : "+###############"
                }
                allowEmptyFormatting
                error={form.formState.errors.phoneNumber?.message}
                type="tel"
                sx={{ marginBottom: 3 }}
                {...form.register("phoneNumber", {
                  required: "This field is required",
                })}
              />

              <CTextField
                fullWidth
                select
                value={form.watch("employmentType")}
                label="Employment Type"
                error={form.formState.errors.employmentType?.message}
                onChange={({ target }) =>
                  form.setValue("employmentType", target.value)
                }
                sx={{ marginBottom: 3 }}
              >
                <MenuItem value="Full Time Employed/ W2">
                  Full Time Employed/ W2
                </MenuItem>
                <MenuItem value="Self Employed">Self Employed</MenuItem>
              </CTextField>

              <Box
                width={"100%"}
                display={"flex"}
                alignItems={"flex-start"}
                flexDirection={{ xs: "column", md: "row" }}
              >
                <Box width={{ xs: "100%", md: "50%" }} mr={{ xs: 0, md: 1 }}>
                  <CNumberField
                    fullWidth
                    label="Social Security Number"
                    type="text"
                    format={"###-##-####"}
                    placeholder="XXX-XX-XXXX"
                    sx={{ marginBottom: 3 }}
                    error={form.formState.errors.socialSecurityNumber?.message}
                    {...form.register("socialSecurityNumber", {
                      required: "This field is required",
                      validate: (newValue) =>
                        newValue.replace(/ /g, "").length < 11
                          ? "This field is required"
                          : undefined,
                    })}
                  />
                </Box>
                <Box width={{ xs: "100%", md: "50%" }} ml={{ xs: 0, md: 1 }}>
                  <CTextField
                    fullWidth
                    type="date"
                    label="Date of birth"
                    error={form.formState.errors.dateOfBirth?.message}
                    {...form.register("dateOfBirth", {
                      required: "This field is required",
                    })}
                    sx={{ marginBottom: 3 }}
                  />
                </Box>
              </Box>

              <CAddressAutocomplete
                label="Address"
                placeholder="Please the enter the address of where you live."
                address={form.watch("address")}
                setAddress={(newAddress) =>
                  form.setValue("address", newAddress)
                }
                sx={{ marginBottom: 3 }}
              />

              <CPasswordField
                label="Create Password"
                {...form.register("password", {
                  required: {
                    value: true,
                    message: "Password is required.",
                  },
                  minLength: {
                    value: 9,
                    message: "The password must be at least 9 characters long.",
                  },
                  maxLength: {
                    value: 100,
                    message: "The password must be less than 100 characters.",
                  },
                  pattern: {
                    value: regexPassword,
                    message: passwordMsgHelper,
                  },
                })}
                error={form.formState.errors.password?.message}
                helperText={passwordMsgHelper}
                placeholder="Type in here"
                fullWidth
              />
              <List
                disablePadding
                sx={{ fontSize: { xs: "1rem", md: "1.4rem" } }}
              >
                <ListItem disableGutters alignItems="flex-start" sx={{ mt: 1 }}>
                  <Box display="inline" fontFamily="Pro Display Semibold">
                    <Checkbox
                      sx={{ pl: 0 }}
                      onClick={() =>
                        form.setValue("agree", !form.getValues().agree)
                      }
                    />
                  </Box>
                  <Typography
                    mt={0.8}
                    variant="subtitle1"
                    color={color.black}
                    textAlign={"justify"}
                  >
                    I agree to the{" "}
                    <Link
                      color={color.purple}
                      textAlign={"justify"}
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                      onClick={() => form.setOpenedDialog("terms")}
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      color={color.purple}
                      textAlign={"justify"}
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                      onClick={() => form.setOpenedDialog("privacy")}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                </ListItem>
                <ListItem disableGutters alignItems="flex-start">
                  <Box display="inline" fontFamily="Pro Display Semibold">
                    <Checkbox
                      sx={{ pl: 0 }}
                      onClick={() =>
                        form.setValue("certify", !form.getValues().certify)
                      }
                    />
                  </Box>
                  <Typography
                    mt={0.8}
                    variant="subtitle1"
                    color={color.black}
                    textAlign={"justify"}
                  >
                    I certify that by checking this box I have read{" "}
                    <Link
                      color={color.purple}
                      textAlign={"justify"}
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                      onClick={() => form.setOpenedDialog("communications")}
                    >
                      NFTYDoor’s Electronic Communications Policy
                    </Link>
                    , and consent to receive all legally required notices and
                    disclosures and other communications (“Communications”) from
                    NFTYDoor LLC electronically and not on paper. I also confirm
                    I am able to access, view, retain and print all of the
                    Communications NFTYDoor LLC provides me in electronic form.
                  </Typography>
                </ListItem>
              </List>
              <Typography
                my={"1rem"}
                color={color.gray}
                variant="subtitle2"
                textAlign={"justify"}
              >
                By pressing the “I agree” button immediately following this
                notice, I am providing ‘written instructions’ to NFTYDoor under
                the Fair Credit Reporting Act authorizing NFTYDoor to obtain
                information from my personal credit profile or other information
                from Experian. I authorize NFTYDoor to obtain such information
                solely to conduct a pre-qualification for credit and acknowledge
                this will not affect my credit score.
              </Typography>
              <CButton
                fullWidth
                type="submit"
                variant="contained"
                loading={form.formState.isSubmitting || user.isGettingLoggedIn}
                disabled={!(form.watch("agree") && form.watch("certify"))}
                sx={{ mt: 4, mb: 2, fontSize: 18, textTransform: "inherit" }}
              >
                I Agree
              </CButton>
            </form>
          </Box>
        </Box>
      </Container>
      <CVerificationDialog
        phoneNumber={form.watch("phoneNumber")}
        open={form.openedDialog === "verify"}
        email={invite?.email?.trim()}
        onConfirmed={() => {
          form.setOpenedDialog(undefined);
          user.login(invite?.email as string, form.watch("password"));
        }}
      />
      <CDialog
        icon="cancel"
        description={form.errorMessage}
        open={form.openedDialog === "error"}
      >
        <CButton
          fullWidth
          variant="contained"
          onClick={() => form.setOpenedDialog(undefined)}
        >
          Accept
        </CButton>
      </CDialog>
      <CTermsDialog
        open={form.openedDialog === "terms"}
        handleClose={() => form.setOpenedDialog(undefined)}
      />
      <CPrivacyDialog
        open={form.openedDialog === "privacy"}
        handleClose={() => form.setOpenedDialog(undefined)}
      />
      <CCommunicationsDialog
        open={form.openedDialog === "communications"}
        handleClose={() => form.setOpenedDialog(undefined)}
      />
    </Fragment>
  );
};

export default CoborrowerRegisterForm;
